import React, { FunctionComponent, useEffect, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAddOn } from "@/hooks/useAddOn";
import { apiErrorMessage } from "@/util/apiError";
import { AddOnType } from "@/enum/AddOnType";
import { RecordingAction } from "@/enum/RecordingAction";
import { RecordingPolicy } from "@/enum/RecordingPolicy";
import { RecordingState } from "@/enum/RecordingState";
import { useGetCallQuery, useGetHomeQuery, useSetCallRecordingStateMutation } from "@/store/rest_api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface RecordingPillProps {
  callId: string | null;
}

const RecordingPill: FunctionComponent<RecordingPillProps> = ({ callId }) => {
  const { data: user } = useGetHomeQuery();
  const { data: call } = useGetCallQuery(callId ?? "", { skip: !callId });
  const hasRecording = useAddOn(AddOnType.CALL_RECORDING);
  const [setRecordingState, { isLoading }] = useSetCallRecordingStateMutation();
  const [state, setState] = useState<RecordingState>(RecordingState.OFF);

  const policy = user?.activeOrganisation?.recordingPolicy ?? RecordingPolicy.ALWAYS;

  const apply = async (action: RecordingAction, nextState: RecordingState) => {
    if (!callId) {
      return;
    }
    try {
      await setRecordingState({ id: callId, action }).unwrap();
      setState(nextState);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to change recording"));
    }
  };

  useEffect(() => {
    if (call?.recordingState) {
      setState(call.recordingState);
    }
  }, [call?.recordingState]);

  if (!hasRecording || policy === RecordingPolicy.NEVER) {
    return null;
  }

  const label = state === RecordingState.PAUSED ? "Paused" : "REC";
  const pillClass =
    state === RecordingState.RECORDING
      ? "status-pill-destructive"
      : state === RecordingState.PAUSED
        ? "status-pill-warning"
        : "status-pill-neutral";

  const pill = (
    <span
      className={cn(pillClass, "shrink-0 gap-1.5 tabular-nums")}
      title={state === RecordingState.OFF ? (call?.recordingSkippedReason ?? undefined) : undefined}
    >
      <span
        className={cn("h-2 w-2 rounded-full bg-current", state === RecordingState.RECORDING && "animate-pulse")}
      />
      {label}
    </span>
  );

  if (policy === RecordingPolicy.ALWAYS) {
    return pill;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger disabled={isLoading || !callId} className="disabled:opacity-60">
        {pill}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {state === RecordingState.OFF && (
          <DropdownMenuItem onClick={() => apply(RecordingAction.START, RecordingState.RECORDING)}>
            Start recording
          </DropdownMenuItem>
        )}
        {state === RecordingState.RECORDING && (
          <DropdownMenuItem onClick={() => apply(RecordingAction.PAUSE, RecordingState.PAUSED)}>
            Pause recording
          </DropdownMenuItem>
        )}
        {state === RecordingState.PAUSED && (
          <DropdownMenuItem onClick={() => apply(RecordingAction.RESUME, RecordingState.RECORDING)}>
            Resume recording
          </DropdownMenuItem>
        )}
        {state !== RecordingState.OFF && (
          <DropdownMenuItem onClick={() => apply(RecordingAction.STOP, RecordingState.OFF)}>
            Stop recording
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RecordingPill;
